/**
 * Which pages are drawn inside the admin app shell — the sidebar (AppSidebar)
 * beside the content slot (SidebarInset) — for the route gate
 * (components/auth/route-gate.tsx).
 *
 * The gate is mounted in the root layout, above every module layout, so it
 * cannot see whether the layout under it draws the shell. Where one does, the
 * gate hands its refusal (and its wait for the grants) to the shell's content
 * slot, and the sidebar stays (owner, 2026-10-06: a person refused a page must
 * be able to navigate away). Anywhere else it keeps the full-screen panel.
 *
 * One entry per top-level app directory whose own layout renders
 * `<SidebarInset>`. lib/auth/__tests__/route-shell.guard.spec.ts reads the
 * layouts on disk and fails when this list and they disagree: a directory
 * listed without a slot would show NO refusal (the page would render), and a
 * shell left out would lose its sidebar on refusal.
 */
export const SHELL_ROOTS: readonly string[] = [
  'announcements',
  'company-dashboard',
  'content-studio',
  'dashboard',
  'deepfake',
  'er',
  'esa',
  'feedback',
  'gamification',
  'jit-training',
  'lms',
  'phishing',
  'phishtrainer',
  'policies',
  'recipients',
  'reports',
  'review-queue',
  'settings',
  'smishing',
  'surveys',
  'vcro',
  'vishing',
  'workflows',
]

/**
 * Pages that hold the route gate themselves. A link to one of these can name a workspace other than the one on screen, and the gate
 * answers for the workspace on screen: at the root it would refuse a person who has every right in the workspace the link names, on a
 * page with no shell, so with no switcher to get out with. The page settles the workspace first, then asks the same verdict
 * (useRouteVerdict) and draws the same panels. Its requirement stays in lib/auth/nav-permissions.ts.
 */
export const SELF_GATED_ROUTES: readonly string[] = ['/setup/gmail-addon']

/** Does the page at `pathname` hold the route gate itself? (Query and hash ignored.) */
export function routeGatesItself(pathname: string): boolean {
  return SELF_GATED_ROUTES.includes(pathname.split(/[?#]/)[0])
}

/** Is the page at `pathname` drawn inside the app shell? (Query and hash ignored.) */
export function routeHasShell(pathname: string): boolean {
  const first = pathname.split(/[?#]/)[0].split('/').filter(Boolean)[0]
  return first !== undefined && SHELL_ROOTS.includes(first)
}
